import { useTranslation } from 'next-i18next'
import PhoneIcon from '@material-ui/icons/Phone'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import Link from '../utility/CustomLink'
import styles from './FooterNew.module.scss'

// Real routes/links/assets are reused verbatim from components/common/Footer.js
// (the shared footer that the OLD homepage still uses, unmodified) — only the
// visual treatment differs here to match the /new mockup's blue rounded card.
//
// Styling was migrated from makeStyles/emotion-JSS to a static CSS Module
// (FooterNew.module.scss) — zero runtime CSS-in-JS. The two MUI SvgIcons are
// kept (they carry no per-page makeStyles). Visual output is unchanged.
const menuLinks = [
  { href: '/', key: 'home' },
  { href: '/service', key: 'shippop-services' },
  { href: '/business', key: 'corporate' },
  // Restore the developer/API entry point (dropped from the new footer): helps
  // Business/API customers reach the API page and adds an SEO internal link.
  { href: '/for-developers', key: 'for-developers' },
  { href: '/tracking', key: 'check-status' },
  { href: '/about', key: 'about-us' },
  { href: '/faq', key: 'faq' },
  { href: '/contact', key: 'contact-short' },
]

// Matches the mockup's "ช่วยเหลือ" column exactly (3 items).
const helpLinks = [
  { href: '/faq', key: 'faq-domestic' },
  { href: '/inter/faq', key: 'faq-inter' },
  { href: '/nearby-dropoff', key: 'dropoff' },
]

// NEW is the site-wide default now, so every internal nav link can use a
// clean href — no ?new needed.
const toNew = (href) => href

const FooterNew = () => {
  const { t } = useTranslation('footer')
  return (
    <footer className={styles.root}>
      <div className={styles.container}>
        <div className={styles.grid}>
          <div>
            <img
              src="/static/img/sp-logo.svg"
              alt="SHIPPOP"
              className={styles.logo}
              width="178"
              height="32"
              loading="lazy"
              decoding="async"
            />
            <p className={styles.address}>
              {t('address-1')}
              <br />
              {t('address-2')}
              <br />
              {t('address-3')}
              <br />
              {t('address-4')}
              <br />
              {t('address-5')}
            </p>
          </div>

          <div>
            <h3 className={styles.colHeading}>{t('menu')}</h3>
            <ul className={styles.list}>
              {menuLinks.map((item) =>
                item.external ? (
                  <li key={item.key}>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.link}
                    >
                      {t(item.key)}
                    </a>
                  </li>
                ) : (
                  <li key={item.key}>
                    <Link href={toNew(item.href)} passHref>
                      <a className={styles.link}>{t(item.key)}</a>
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>

          <div>
            <h3 className={styles.colHeading}>{t('help')}</h3>
            <ul className={styles.list}>
              {helpLinks.map((item) => (
                <li key={item.key}>
                  <Link href={toNew(item.href)} passHref>
                    <a className={styles.link}>{t(item.key)}</a>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className={styles.colHeading}>{t('contact-us')}</h3>
            <div className={styles.contactRow}>
              <PhoneIcon className={styles.contactIcon} />
              <div className={styles.contactText}>
                <a href="tel:+6620966629" className={styles.contactLink}>
                  (+66) 2-096-6629
                </a>
                <br />
                {t('business-time-1')}
              </div>
            </div>
            <div className={styles.contactRow}>
              <MailOutlineIcon className={styles.contactIcon} />
              <div className={styles.contactText}>
                <a href="mailto:cs@shippop.com" className={styles.contactLink}>
                  cs@shippop.com
                </a>
              </div>
            </div>
            <h4 className={styles.lineHeading}>{t('add-friend-line')}</h4>
            <a
              href="https://line.me/R/ti/p/@shippop"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.lineButton}
            >
              <img
                src="/static/img/icons/line_add_friend_button.svg"
                alt="Add Friend SHIPPOP"
                width="180"
                height="46"
                loading="lazy"
                decoding="async"
              />
            </a>
          </div>
        </div>

        <hr className={styles.divider} />

        <div className={styles.bottomBar}>
          <p className={styles.copyright}>
            © Copyright 2015 - {new Date().getFullYear()} All Rights Reserved by SHIPPOP
          </p>
          <div className={styles.legalLinks}>
            <Link href="/terms" passHref>
              <a className={styles.legalLink}>{t('tos')}</a>
            </Link>
            <Link href="/privacy" passHref>
              <a className={styles.legalLink}>{t('privacy')}</a>
            </Link>
            {/*
              NOTE: the shared Footer's "ตั้งค่าคุกกี้" opens the PDPA popup via
              a handleClickSetCookies callback owned by Layout's local state.
              Layout doesn't expose that to page children/FooterNew, and
              wiring it through would mean changing the shared Layout.js,
              which is out of this component's scope — so this is a plain
              (non-functional) label for now rather than a fake/broken click
              handler. See report for detail.
            */}
            <span className={styles.legalLink}>{t('cookies-setting')}</span>
          </div>
          <div className={styles.socialRow}>
            <a
              href="https://www.facebook.com/shippop.official"
              target="_blank"
              rel="noopener noreferrer"
              title="SHIPPOP Facebook page"
              className={styles.socialIcon}
            >
              <img
                src="/static/img/icons/facebook.svg"
                alt="facebook"
                width="16"
                height="16"
                loading="lazy"
                decoding="async"
              />
            </a>
            <a
              href="https://www.youtube.com/channel/UCqthHHdPcpFlI8gTA6TAOdw"
              target="_blank"
              rel="noopener noreferrer"
              title="SHIPPOP Youtube channel"
              className={styles.socialIcon}
            >
              <img
                src="/static/img/icons/youtube.svg"
                alt="youtube"
                width="16"
                height="16"
                loading="lazy"
                decoding="async"
              />
            </a>
            <a
              href="https://instagram.com/shippopcom"
              target="_blank"
              rel="noopener noreferrer"
              title="SHIPPOP Instagram"
              className={styles.socialIcon}
            >
              <img
                src="/static/img/icons/instagram.svg"
                alt="instagram"
                width="16"
                height="16"
                loading="lazy"
                decoding="async"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default FooterNew
